import type { TalkToSalesFormData } from '../components/TalkToSalesModal/TalkToSalesModal';
import { sessionManager } from '../utils/sessionUtils';

export interface LeadRequest {
  full_name: string;
  email: string;
  phone?: string;
  zip_code?: string;
  status?: "active" | "archived" | "converted";
  metadata?: {
    source?: string;
    notes?: string;
    sessionId?: string;
  };
}

export interface LeadResponse {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  zip_code?: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface LeadApiErrorData {
  message: string;
  status?: number;
  details?: unknown;
}

export interface LeadCreationResponse {
  success: boolean;
  lead?: LeadResponse;
  error?: string;
  timestamp: string;
}

class LeadService {
  private baseUrl = '/api';

  /**
   * Transforms frontend form data to backend LeadRequest format
   */
  private transformFormDataToLeadRequest(formData: TalkToSalesFormData): LeadRequest {
    const sessionId = sessionManager.getSessionId();
    
    const leadRequest: LeadRequest = {
      full_name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
      email: formData.email.trim(),
      status: "active",
      metadata: {
        source: "talk_to_sales_modal",
        notes: "Lead generated from Talk to Sales form",
        sessionId
      }
    };

    // Only include phone if provided
    if (formData.phoneNumber && formData.phoneNumber.trim()) {
      leadRequest.phone = formData.phoneNumber.trim();
    }

    // Only include zip code if provided
    if (formData.zipCode && formData.zipCode.trim()) {
      leadRequest.zip_code = formData.zipCode.trim();
    }

    return leadRequest;
  }

  /**
   * Creates a new lead by sending data to the backend
   */
  async createLead(formData: TalkToSalesFormData): Promise<LeadResponse> {
    try {
      const leadRequest = this.transformFormDataToLeadRequest(formData);
      
      const response = await fetch(`${this.baseUrl}/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(leadRequest),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error occurred' }));
        throw new LeadApiError(
          errorData.error || `Request failed with status ${response.status}`,
          response.status
        );
      }

      const result: LeadCreationResponse = await response.json();
      
      if (!result.success || !result.lead) {
        throw new LeadApiError(
          result.error || 'Lead creation failed',
          400
        );
      }

      return result.lead;
    } catch (error) {
      if (error instanceof LeadApiError) {
        throw error;
      }
      
      if (error instanceof Error) {
        throw new LeadApiError(`Network error: ${error.message}`);
      }
      
      throw new LeadApiError('An unexpected error occurred');
    }
  }

  /**
   * Check if the leads API is available
   */
  async healthCheck(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}/health`);
      return response.ok;
    } catch {
      return false;
    }
  }
}

export class LeadApiError extends Error {
  public status?: number;
  public details?: unknown;

  constructor(message: string, status?: number, details?: unknown) {
    super(message);
    this.name = 'LeadApiError';
    this.status = status;
    this.details = details;
  }
}

export const leadService = new LeadService();
